import React, { forwardRef } from 'react';
import type { LinkProps as MuiLinkProps } from '@mui/material';
import { Link as MuiLink } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/

/**
 * Explicit Link props based on actual usage in the codebase.
 */
export interface LinkProps {
  /** The URL the link points to */
  href?: MuiLinkProps['href'];
  /** Callback fired when the link is clicked */
  onClick?: MuiLinkProps['onClick'];
  /** CSS class name to apply to the root element */
  className?: MuiLinkProps['className'];
  /** Inline styles applied to the root element */
  style?: MuiLinkProps['style'];
  /** The rel attribute applied to the underlying anchor element */
  rel?: MuiLinkProps['rel'];
  /** The target attribute applied to the underlying anchor element */
  target?: MuiLinkProps['target'];
  /** The color of the link */
  color?: MuiLinkProps['color'];
  /** The component used for the root node (e.g. 'button' to render a button element) */
  component?: React.ElementType;
  /** Controls when the link should have an underline */
  underline?: MuiLinkProps['underline'];
  /** Applies the theme typography styles */
  variant?: MuiLinkProps['variant'];
  /** The content of the link */
  children?: MuiLinkProps['children'];
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { href, onClick, className, style, rel, target, color, component, underline, variant, children, ...restProps },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Link: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the LinkProps interface and to the component's signature.`,
        true
      );
    }
    // Spread the validated props rather than passing `component` as an explicit JSX
    // attribute: MUI's OverridableComponent overloads reject an optional `component`
    // attribute, but accept it via a spread (see the Button wrapper).
    const forwardedProps = {
      href,
      onClick,
      className,
      style,
      rel,
      target,
      color,
      component,
      underline,
      variant,
      ...restProps,
    };
    return (
      <MuiLink ref={ref} {...forwardedProps}>
        {children}
      </MuiLink>
    );
  }
);

Link.displayName = 'Link';

export default Link;
